<template>
    <div class="shift-detail pa-4">
        <div v-if="loading && !detail" class="py-6 text-center">
            <v-progress-circular indeterminate color="primary"/>
        </div>

        <v-alert v-else-if="loadError" type="error" variant="tonal" density="compact">
            {{ loadError }}
            <template #append>
                <v-btn size="small" variant="text" @click="load">Retry</v-btn>
            </template>
        </v-alert>

        <template v-else-if="detail">
            <!-- At a glance -->
            <div class="d-flex flex-wrap ga-3 mb-4">
                <div v-for="stat in stats" :key="stat.label" class="stat-tile">
                    <div class="text-caption text-medium-emphasis">{{ stat.label }}</div>
                    <div class="text-subtitle-1 font-weight-bold">{{ stat.value }}</div>
                </div>
            </div>

            <!-- Timeline: the shift, its breaks and its trips on one bar -->
            <div class="mb-5">
                <div class="d-flex justify-space-between text-caption text-medium-emphasis mb-1">
                    <span>{{ f.fmtTime(shift.started_at) }}</span>
                    <span>{{ shift.ended_at ? f.fmtTimeAfter(shift.ended_at, shift.started_at) : 'now' }}</span>
                </div>
                <div class="timeline">
                    <div class="lane lane-shift" role="img" :aria-label="timelineLabel">
                        <div v-for="(b, i) in breakBars" :key="'b' + i" class="seg seg-break"
                             :style="{left: b.left + '%', width: b.width + '%'}">
                            <v-tooltip activator="parent" location="top">{{ b.label }}</v-tooltip>
                        </div>
                    </div>
                    <div class="lane lane-trips">
                        <div v-for="t in tripBars" :key="'t' + t.id" class="seg"
                             :class="t.byDriver ? 'seg-trip' : 'seg-trip-other'"
                             :style="{left: t.left + '%', width: t.width + '%'}"
                             @click="openTrip(t.id)">
                            <v-tooltip activator="parent" location="bottom">{{ t.label }}</v-tooltip>
                        </div>
                    </div>
                </div>
                <div class="d-flex flex-wrap ga-4 text-caption text-medium-emphasis mt-2">
                    <span class="legend"><i class="dot dot-shift"/>Working</span>
                    <span class="legend"><i class="dot dot-break"/>Break</span>
                    <span class="legend"><i class="dot dot-trip"/>Driving</span>
                    <span v-if="tripBars.some(t => !t.byDriver)" class="legend">
                        <i class="dot dot-trip-other"/>Vehicle driven by someone else
                    </span>
                </div>
            </div>

            <!-- Review: what was flagged and the decision -->
            <v-card v-if="shift.can_review" variant="outlined" color="error" rounded="lg" class="mb-5">
                <v-card-item>
                    <template #prepend>
                        <v-icon color="error">mdi-alert-circle</v-icon>
                    </template>
                    <v-card-title class="text-subtitle-1 font-weight-bold">This shift needs your review</v-card-title>
                    <v-card-subtitle class="text-wrap">
                        Pay follows the end time. Confirm it, or set the time the driver really stopped.
                    </v-card-subtitle>
                </v-card-item>
                <v-card-text class="text-high-emphasis">
                    <ul class="reasons mb-4">
                        <li v-for="(r, i) in shift.review_reasons" :key="i">{{ reasonText(r, f.fmtDayTime) }}</li>
                    </ul>

                    <v-radio-group v-model="choice" hide-details class="mb-2">
                        <v-radio v-for="opt in endOptions" :key="opt.key" :value="opt.key">
                            <template #label>
                                <div>
                                    <div class="font-weight-medium">{{ opt.title }}</div>
                                    <div class="text-caption text-medium-emphasis">{{ opt.subtitle }}</div>
                                </div>
                            </template>
                        </v-radio>
                    </v-radio-group>

                    <div v-if="choice === 'custom'" class="ml-8 mb-3">
                        <div class="d-flex flex-wrap align-center ga-3">
                            <v-text-field v-model="customEnd" type="datetime-local" label="Shift ended at"
                                          density="compact" variant="outlined" hide-details
                                          :min="customMin" :max="customMax" :error="!!customProblem"
                                          style="max-width: 260px"/>
                            <span class="text-caption text-medium-emphasis">Times are in {{ zoneLabel }}</span>
                        </div>
                        <div v-if="customProblem" class="text-caption text-error mt-1">{{ customProblem }}</div>
                    </div>

                    <div v-if="chosenEnd && !customProblem" class="text-body-2 mb-3">
                        From clock-in to this end: <strong>{{ fmtDuration(lengthWith(chosenEnd)) }}</strong>.
                        <span v-if="Number(shift.break_seconds) > 0" class="text-medium-emphasis">
                            Breaks in that time are taken off for worked time.</span>
                    </div>

                    <v-textarea v-model="note" label="Note (optional)" placeholder="e.g. Confirmed with the driver by phone"
                                variant="outlined" density="compact" rows="2" auto-grow counter="500"
                                maxlength="500" hide-details="auto"/>

                    <v-alert v-if="reviewError" type="error" variant="tonal" density="compact" class="mt-3">
                        {{ reviewError }}
                    </v-alert>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-spacer/>
                    <v-btn color="primary" variant="flat" :loading="saving" :disabled="saving || !canSubmit"
                           prepend-icon="mdi-check" @click="submitReview">
                        {{ sendsNewEnd ? 'Save end time' : 'Confirm shift' }}
                    </v-btn>
                </v-card-actions>
            </v-card>

            <!-- Flagged while still running -->
            <v-alert v-else-if="shift.needs_review && shift.status !== 'ended'" type="warning" variant="tonal"
                     density="compact" class="mb-5">
                Flagged for review. You can settle it once the shift has ended.
                <ul class="reasons mt-1">
                    <li v-for="(r, i) in shift.review_reasons" :key="i">{{ reasonText(r, f.fmtDayTime) }}</li>
                </ul>
            </v-alert>

            <v-alert v-if="conflictNote" type="info" variant="tonal" density="compact" class="mb-5">
                {{ conflictNote }}
            </v-alert>

            <!-- A running shift holds its truck: only one driver may be clocked
                 in on a truck. Releasing it lets another driver clock in. -->
            <v-alert v-if="shift.status !== 'ended' && shift.asset_label" type="info" variant="tonal"
                     density="compact" class="mb-5" icon="mdi-truck-outline">
                {{ shift.asset_label }} is held by this shift. No other driver can clock in to it until this
                driver clocks out or you release it.
                <template #append>
                    <v-btn size="small" variant="text" color="primary" :loading="releasing"
                           @click="releaseDialog = true">Release truck</v-btn>
                </template>
            </v-alert>
            <v-dialog v-model="releaseDialog" max-width="440">
                <v-card>
                    <v-card-title class="text-subtitle-1 font-weight-bold">Release {{ shift.asset_label }}?</v-card-title>
                    <v-card-text class="text-body-2">
                        The driver stays on shift without a truck, and the shift is flagged for your review.
                        Trips on {{ shift.asset_label }} from now on no longer count as this shift's.
                        Another driver can then clock in to it.
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer/>
                        <v-btn variant="text" @click="releaseDialog = false">Cancel</v-btn>
                        <v-btn color="primary" variant="flat" :loading="releasing" :disabled="releasing"
                               @click="releaseVehicle">Release</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Review history, oldest first -->
            <div v-if="reviews.length" class="mb-5">
                <div class="text-subtitle-2 font-weight-bold mb-2">Reviews</div>
                <div v-for="(r, i) in reviews" :key="i" class="item-row mb-2 align-start">
                    <v-icon size="18" color="primary" class="mt-1">mdi-check-decagram</v-icon>
                    <div class="flex-grow-1">
                        <div class="text-body-2">
                            <strong>{{ r.reviewed_by_name || 'A manager' }}</strong>
                            {{ !r.to_ended_at
                                ? 'released the truck'
                                : r.changed
                                    ? `changed the end from ${f.fmtDayTime(r.from_ended_at)} to ${f.fmtDayTime(r.to_ended_at)}`
                                    : `confirmed the end, ${f.fmtDayTime(r.to_ended_at)}` }}
                        </div>
                        <div class="text-caption text-medium-emphasis">{{ f.fmtDayTime(r.reviewed_at) }}</div>
                        <div v-if="r.note" class="text-body-2 font-italic mt-1">"{{ r.note }}"</div>
                    </div>
                </div>
            </div>

            <v-row>
                <!-- Breaks -->
                <v-col cols="12" md="5">
                    <div class="text-subtitle-2 font-weight-bold mb-2">Breaks</div>
                    <div v-if="breaks.length" class="d-flex flex-column ga-2">
                        <div v-for="(b, i) in breaks" :key="i" class="item-row">
                            <v-icon size="18" color="warning">mdi-coffee</v-icon>
                            <span>{{ f.fmtTime(b.started_at) }} to {{ b.open ? 'now' : f.fmtTimeAfter(b.ended_at, b.started_at) }}</span>
                            <v-spacer/>
                            <v-chip v-if="b.open" size="x-small" color="warning" variant="tonal">On break</v-chip>
                            <span class="font-weight-medium">{{ fmtDuration(b.seconds) }}</span>
                        </div>
                    </div>
                    <div v-else-if="Number(shift.break_seconds) > 0" class="text-body-2 text-medium-emphasis">
                        {{ fmtDuration(shift.break_seconds) }} on break in total. Times of each break are
                        kept for newer shifts only.
                    </div>
                    <div v-else class="text-body-2 text-medium-emphasis">No breaks taken.</div>
                    <div v-if="breaks.length && !detail.breaks_itemised" class="text-caption text-medium-emphasis mt-2">
                        Total on break: {{ fmtDuration(shift.break_seconds) }}. Some breaks were recorded before
                        break times were kept.
                    </div>
                </v-col>

                <!-- Trips -->
                <v-col cols="12" md="7">
                    <div class="text-subtitle-2 font-weight-bold mb-2">Trips during the shift</div>
                    <div v-if="!trips.length" class="text-body-2 text-medium-emphasis">
                        No vehicle trips were recorded during this shift.
                    </div>
                    <div v-else class="d-flex flex-column ga-2">
                        <div v-for="t in trips" :key="t.id">
                            <div class="item-row item-click" role="button" tabindex="0"
                                 :aria-expanded="openTripId === t.id"
                                 @click="openTrip(t.id)" @keydown.enter.prevent="openTrip(t.id)"
                                 @keydown.space.prevent="openTrip(t.id)">
                                <v-icon size="18" :color="t.by_driver ? 'primary' : 'deep-purple'">mdi-steering</v-icon>
                                <div class="flex-grow-1 min-w-0">
                                    <div class="text-body-2">
                                        {{ f.fmtTime(t.start_time) }} to {{ f.fmtTimeAfter(t.end_time, t.start_time) }}
                                        <span class="text-medium-emphasis">&middot; {{ t.asset_label || 'Vehicle' }}</span>
                                        <v-chip v-if="!t.by_driver" size="x-small" color="deep-purple" variant="tonal"
                                                class="ml-1">Other driver</v-chip>
                                        <v-chip v-if="t.tag_disagrees" size="x-small" color="warning" variant="tonal"
                                                class="ml-1" title="The driver tag the tracker saw belongs to another driver">
                                            Tag: another driver
                                        </v-chip>
                                    </div>
                                    <div v-if="t.start_location_text || t.end_location_text"
                                         class="text-caption text-medium-emphasis text-truncate">
                                        {{ t.start_location_text || 'Unknown' }} &rarr; {{ t.end_location_text || 'Unknown' }}
                                    </div>
                                </div>
                                <div class="text-right text-no-wrap">
                                    <div class="text-body-2 font-weight-medium">{{ unitifyDistance(t.distance_km, 'distance', 'km') }}</div>
                                    <div class="text-caption text-medium-emphasis">{{ fmtDuration(t.duration) }}</div>
                                </div>
                                <v-icon size="18">{{ openTripId === t.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                            </div>
                            <div v-if="openTripId === t.id && mapDevice" class="trip-map mt-2">
                                <TripRouteMap :selected-device="mapDevice" :selected-trip="mapTrip"/>
                            </div>
                        </div>
                    </div>
                </v-col>
            </v-row>
        </template>
    </div>
</template>

<script setup>
import {computed, onMounted, provide, ref, watch} from "vue";
import {refreshMenuCounts} from "@/composables/useMenuCounts";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {strOperations} from "@/composables/globals";
import {companyTimezone, timezoneLongName, toZonedInput, zonedInputToUtcIso} from "@/composables/useTimezone";
import TripRouteMap from "@/components/cards/TripRouteMap.vue";
import {apiError, fmtDuration, reasonText, shiftFormatter} from "./shiftFormat";

const props = defineProps({
    shiftId: {type: [Number, String], required: true},
    // For a platform admin, the company the shift belongs to.
    companyId: {type: [Number, String], default: null},
});
const emit = defineEmits(["reviewed"]);

// TripRouteMap reads live updates by inject; outside the Live view these are
// inert stubs.
provide("latestLocations", ref(new Map()));
provide("lastMqttUpdate", ref(null));

const {showSnackbar} = useSnackbarStore();
const {unitifyDistance} = strOperations();

const detail = ref(null);
const loading = ref(false);
const loadError = ref("");
const openTripId = ref(null);

const shift = computed(() => detail.value?.shift ?? {});
const breaks = computed(() => detail.value?.breaks ?? []);
const trips = computed(() => detail.value?.trips ?? []);
const reviews = computed(() => detail.value?.reviews ?? []);

// The fleet's zone, as the API sends it; the signed-in company's until then.
const zone = computed(() => detail.value?.company_timezone || companyTimezone());
const f = computed(() => shiftFormatter(zone.value));
const zoneLabel = computed(() => timezoneLongName(zone.value));

const load = ({keepNote = false} = {}) => {
    loading.value = true;
    loadError.value = "";
    return send("get", `fleet/duty/sessions/${props.shiftId}`, props.companyId ? {company: props.companyId} : null)
        .then((r) => {
            detail.value = r.data.data;
            resetReview({keepNote});
        })
        .catch((e) => {
            loadError.value = apiError(e, "This shift could not be loaded.");
        })
        .finally(() => {
            loading.value = false;
        });
};

// One map at a time. Its props are set once when a trip is opened and keep
// their identity after that: TripRouteMap clears its route whenever
// selectedDevice changes, so a fresh object on a re-render (typing a note) or
// a reload (after a review) would wipe the route off the map.
const mapDevice = ref(null);
const mapTrip = ref(null);

const openTrip = (id) => {
    if (openTripId.value === id) {
        openTripId.value = null;
        mapDevice.value = null;
        mapTrip.value = null;
        return;
    }
    const row = trips.value.find((t) => t.id === id);
    if (!row) return;
    mapDevice.value = {asset_id: row.asset_id, company: shift.value.company_id, slno: null};
    mapTrip.value = {id: row.id};
    openTripId.value = id;
};

// ---- at a glance --------------------------------------------------------

const stats = computed(() => {
    const totals = detail.value?.trip_totals ?? {};
    return [
        {label: "Date", value: f.value.fmtDay(shift.value.started_at)},
        {label: "Worked", value: fmtDuration(shift.value.worked_seconds)},
        {label: "Breaks", value: fmtDuration(shift.value.break_seconds)},
        {label: "Driving", value: fmtDuration(totals.driving_seconds)},
        {label: "Distance", value: unitifyDistance(totals.distance_km ?? 0, "distance", "km")},
        {label: "Trips", value: totals.count ?? 0},
    ];
});

// ---- timeline -------------------------------------------------------------

const span = computed(() => {
    const start = Date.parse(shift.value.started_at);
    const end = shift.value.ended_at ? Date.parse(shift.value.ended_at) : Date.now();
    return {start, end, length: Math.max(1, end - start)};
});

// A segment as left/width percentages of the shift, clamped to it. Very short
// ones keep a sliver of width so they can still be seen and hovered.
const bar = (fromIso, toIso) => {
    const {start, end, length} = span.value;
    const from = Math.max(start, Date.parse(fromIso));
    const to = Math.min(end, toIso ? Date.parse(toIso) : end);
    if (!(to >= from)) return null;
    const left = ((from - start) / length) * 100;
    return {left, width: Math.max(0.6, Math.min(100 - left, ((to - from) / length) * 100))};
};

const breakBars = computed(() => breaks.value.map((b) => {
    const pos = bar(b.started_at, b.open ? null : b.ended_at);
    return pos && {
        ...pos,
        label: `Break ${f.value.fmtTime(b.started_at)} to ${b.open ? 'now' : f.value.fmtTime(b.ended_at)} (${fmtDuration(b.seconds)})`,
    };
}).filter(Boolean));

const tripBars = computed(() => trips.value.map((t) => {
    const pos = bar(t.start_time, t.end_time);
    return pos && {
        ...pos,
        id: t.id,
        byDriver: t.by_driver,
        label: `${t.asset_label || 'Trip'} ${f.value.fmtTime(t.start_time)} to ${f.value.fmtTime(t.end_time)}`
            + (t.by_driver ? "" : " (another driver)"),
    };
}).filter(Boolean));

const timelineLabel = computed(() =>
    `Shift from ${f.value.fmtTime(shift.value.started_at)} to `
    + `${shift.value.ended_at ? f.value.fmtTime(shift.value.ended_at) : 'now'}, `
    + `${breakBars.value.length} breaks, ${tripBars.value.length} trips`);

// ---- review ---------------------------------------------------------------

const choice = ref("keep");
const customEnd = ref("");
const customStart = ref("");
const note = ref("");
const saving = ref(false);
const reviewError = ref("");
const conflictNote = ref("");

// The earliest end the server will take. When breaks were not recorded one
// by one, the end can only stay or move later (the server refuses earlier).
const earliestEndMs = computed(() => {
    const s = shift.value;
    const start = Date.parse(s.started_at);
    const shrinkable = detail.value?.breaks_itemised || !(Number(s.break_seconds) > 0);
    return shrinkable || !s.ended_at ? start : Math.max(start, Date.parse(s.ended_at));
});
const latestEndMs = computed(() => {
    const capHours = Number(detail.value?.stale_after_hours) || 16;
    return Math.min(Date.parse(shift.value.started_at) + capHours * 3600 * 1000, Date.now());
});

// The end times on offer: the current one, every distinct time the phone
// claimed that the server would accept, and a free choice.
const endOptions = computed(() => {
    const s = shift.value;
    if (!s.ended_at) return [];
    const endMs = Date.parse(s.ended_at);
    const opts = [{
        key: "keep",
        title: `Keep ${f.value.fmtDayTime(s.ended_at)}`,
        subtitle: s.source === "auto" ? "The end the system chose" : "The end on record",
        iso: null,
    }];
    const seen = new Set([endMs]);
    for (const iso of [s.claimed_ended_at, s.furthest_claimed_ended_at, s.last_claimed_ended_at]) {
        const ms = iso ? Date.parse(iso) : NaN;
        if (isNaN(ms) || seen.has(ms) || ms < earliestEndMs.value || ms > latestEndMs.value) continue;
        seen.add(ms);
        opts.push({
            key: `claim:${iso}`,
            title: `Use the driver's clock-out, ${f.value.fmtDayTime(iso)}`,
            subtitle: "The time the driver's phone sent",
            iso,
        });
    }
    opts.push({key: "custom", title: "Another time", subtitle: "Set the end yourself", iso: null});
    return opts;
});

// Whole minutes for the input: the earliest is rounded up and the latest
// down, so every minute the picker allows is one the server accepts.
const customMin = computed(() => toZonedInput(new Date(Math.ceil(earliestEndMs.value / 60000) * 60000).toISOString(), zone.value));
const customMax = computed(() => toZonedInput(new Date(Math.floor(latestEndMs.value / 60000) * 60000).toISOString(), zone.value));

const customIso = computed(() => zonedInputToUtcIso(customEnd.value, zone.value));
// "Another time" left as it opened is the end as it stands: sending it would
// move the end by the seconds the input cannot show.
const customUnchanged = computed(() => customEnd.value === customStart.value);

const customProblem = computed(() => {
    if (choice.value !== "custom" || customUnchanged.value) return "";
    if (!customIso.value) return "Enter a date and time.";
    const ms = Date.parse(customIso.value);
    if (ms < earliestEndMs.value) {
        return earliestEndMs.value > Date.parse(shift.value.started_at)
            ? "This shift's break times were not recorded one by one, so its end can only be kept or moved later."
            : "The end cannot be before the shift started.";
    }
    if (ms > latestEndMs.value) {
        return ms > Date.now()
            ? "The end cannot be in the future."
            : `A shift cannot run longer than ${Number(detail.value?.stale_after_hours) || 16} hours.`;
    }
    return "";
});

const sendsNewEnd = computed(() => {
    if (choice.value === "keep") return false;
    if (choice.value === "custom") return !customUnchanged.value;
    return true;
});

const chosenEnd = computed(() => {
    if (choice.value === "keep" || (choice.value === "custom" && customUnchanged.value)) return shift.value.ended_at;
    if (choice.value === "custom") return customIso.value;
    return endOptions.value.find((o) => o.key === choice.value)?.iso ?? null;
});

const lengthWith = (iso) => Math.max(0, (Date.parse(iso) - Date.parse(shift.value.started_at)) / 1000);

const canSubmit = computed(() => !sendsNewEnd.value || (!!chosenEnd.value && !customProblem.value));

const resetReview = ({keepNote = false} = {}) => {
    choice.value = "keep";
    customStart.value = toZonedInput(shift.value.ended_at, zone.value);
    customEnd.value = customStart.value;
    if (!keepNote) note.value = "";
    reviewError.value = "";
};

// ---- release the truck ----------------------------------------------------

const releaseDialog = ref(false);
const releasing = ref(false);

const releaseVehicle = () => {
    if (releasing.value) return;
    releasing.value = true;
    const body = props.companyId ? {company: props.companyId} : {};
    send("post", `fleet/duty/sessions/${props.shiftId}/release-vehicle`, body)
        .then((r) => {
            detail.value = r.data.data;
            showSnackbar("Truck released");
            emit("reviewed", r.data.data.shift);
        })
        .catch((e) => {
            showSnackbar(apiError(e, "The truck could not be released."), "error");
            // Ended or released meanwhile: show the shift as it is now.
            if (e?.response?.status === 409) load();
        })
        .finally(() => {
            releasing.value = false;
            releaseDialog.value = false;
        });
};

const submitReview = () => {
    if (saving.value || !canSubmit.value) return;
    saving.value = true;
    reviewError.value = "";
    conflictNote.value = "";
    const body = {note: note.value.trim() || null};
    if (props.companyId) body.company = props.companyId;
    // Keeping the end sends no time at all, so a rounding of the displayed
    // minute can never move it.
    if (sendsNewEnd.value) body.ended_at = chosenEnd.value;
    const kept = !sendsNewEnd.value;
    send("post", `fleet/duty/sessions/${props.shiftId}/review`, body)
        .then((r) => {
            detail.value = r.data.data;
            showSnackbar(kept ? "Shift confirmed" : "Shift end saved");
            emit("reviewed", r.data.data.shift);
            refreshMenuCounts();
            resetReview();
        })
        .catch((e) => {
            const message = apiError(e, "The review could not be saved.");
            if (e?.response?.status === 409) {
                // Settled by someone else meanwhile: show the shift as it is
                // now, say why nothing was saved, and update the list row.
                load({keepNote: true}).then(() => {
                    conflictNote.value = message;
                    if (detail.value?.shift) emit("reviewed", detail.value.shift);
                });
            } else {
                reviewError.value = message;
            }
        })
        .finally(() => {
            saving.value = false;
        });
};

watch(() => props.shiftId, () => {
    openTripId.value = null;
    mapDevice.value = null;
    mapTrip.value = null;
    conflictNote.value = "";
    load();
});
onMounted(() => load());
</script>

<style scoped>
.shift-detail {
    background: rgba(var(--v-theme-on-surface), 0.02);
}

.stat-tile {
    min-width: 110px;
    padding: 8px 12px;
    border-radius: 8px;
    background: rgba(var(--v-theme-on-surface), 0.04);
}

.timeline {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.lane {
    position: relative;
    height: 14px;
    border-radius: 7px;
}

.lane-shift {
    background: rgba(var(--v-theme-success), 0.55);
}

.lane-trips {
    background: rgba(var(--v-theme-on-surface), 0.06);
}

.seg {
    position: absolute;
    top: 0;
    bottom: 0;
    border-radius: 7px;
}

/* Striped, not only coloured, so a break reads without telling green from
   orange. */
.seg-break {
    background: repeating-linear-gradient(135deg, rgb(var(--v-theme-warning)) 0 4px,
        rgba(var(--v-theme-warning), 0.6) 4px 8px);
    box-shadow: inset 0 0 0 1px rgba(var(--v-theme-on-surface), 0.35);
}

.seg-trip {
    background: rgb(var(--v-theme-primary));
    cursor: pointer;
}

.seg-trip-other {
    background: #7e57c2;
    cursor: pointer;
}

.legend {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.dot-shift {
    background: rgba(var(--v-theme-success), 0.55);
}

.dot-break {
    background: repeating-linear-gradient(135deg, rgb(var(--v-theme-warning)) 0 2px,
        rgba(var(--v-theme-warning), 0.6) 2px 4px);
    box-shadow: inset 0 0 0 1px rgba(var(--v-theme-on-surface), 0.35);
}

.dot-trip {
    background: rgb(var(--v-theme-primary));
}

.dot-trip-other {
    background: #7e57c2;
}

.reasons {
    padding-left: 20px;
}

.reasons li + li {
    margin-top: 4px;
}

.item-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    background: rgba(var(--v-theme-on-surface), 0.04);
}

.align-start {
    align-items: flex-start;
}

.item-click {
    cursor: pointer;
}

.item-click:hover,
.item-click:focus-visible {
    background: rgba(var(--v-theme-primary), 0.08);
    outline: none;
}

.min-w-0 {
    min-width: 0;
}

/* TripRouteMap's card is sized for a half-height dashboard slot; here it
   fills the box, as on the asset page. */
.trip-map {
    height: 380px;
    border-radius: 8px;
}

.trip-map :deep(.half-card) {
    height: 100% !important;
    display: flex;
    flex-direction: column;
}

.trip-map :deep(.half-card .v-card-title) {
    padding: 4px 8px !important;
}

.trip-map :deep(.half-card .v-card-title h3) {
    font-size: 0.85rem;
}

.trip-map :deep(.half-card .v-card-text) {
    height: auto !important;
    flex: 1;
}
</style>
